<template>
  <div v-click-away="colorOutSize" class="hu-color-picker" :style="colorStyling" :class="{ light: isLightTheme }">
    <div class="gf_color-picker--header">
      <div class="gf_color-picker--header-title">Color Picker</div>
      <div class="gf_color-picker--header-close" @click="colorOutSize">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2.35355 1.64645C2.15829 1.45118 1.84171 1.45118 1.64645 1.64645C1.45118 1.84171 1.45118 2.15829 1.64645 2.35355L7.29289 8L1.64645 13.6464C1.45118 13.8417 1.45118 14.1583 1.64645 14.3536C1.84171 14.5488 2.15829 14.5488 2.35355 14.3536L8 8.70711L13.6464 14.3536C13.8417 14.5488 14.1583 14.5488 14.3536 14.3536C14.5488 14.1583 14.5488 13.8417 14.3536 13.6464L8.70711 8L14.3536 2.35355C14.5488 2.15829 14.5488 1.84171 14.3536 1.64645C14.1583 1.45118 13.8417 1.45118 13.6464 1.64645L8 7.29289L2.35355 1.64645Z"
            fill="white" />
        </svg>
      </div>
    </div>
    <div class="color-set">
      <Saturation
        ref="saturation"
        :color="rgbString"
        :hsv="hsv"
        :size="hueWidth"
        :width="hueWidth"
        @select-saturation="selectSaturation" />
      <Hue ref="hue" :hsv="hsv" :color="rgbString" :width="hueWidth" :height="hueHeight" @select-hue="selectHue" />
      <Alpha
        ref="alpha"
        :color="rgbString"
        :rgba="rgba"
        :width="hueWidth"
        :height="hueHeight"
        @select-alpha="selectAlpha" />
    </div>
    <Box
      name="HEX"
      :color="modelHex"
      :rgba-string="rgbaString"
      :alpha="a"
      @input-alpha="selectAlpha"
      @input-color="inputHex" />
  </div>
</template>

<script>
import mixin from './color/mixin';
import Saturation from './color/Saturation.vue';
import Hue from './color/Hue.vue';
import Alpha from './color/Alpha.vue';
import Box from './color/Box.vue';

export default {
  name: 'GfColorPicker',
  components: {
    Saturation,
    Hue,
    Alpha,
    Box,
  },
  mixins: [mixin],
  props: {
    showFooter: {
      type: Boolean,
      default: false,
    },
    colorStyling: {
      type: Object,
      default: () => {},
    },
    color: {
      type: String,
      default: '#000000',
    },
    theme: {
      type: String,
      default: 'dark',
    },
    suckerHide: {
      type: Boolean,
      default: true,
    },
    suckerCanvas: {
      type: null, // HTMLCanvasElement
      default: null,
    },
    suckerArea: {
      type: Array,
      default: () => [],
    },
    colorsDefault: {
      type: Array,
      default: () => [
        '#252525',
        '#494949',
        '#848484',
        '#F8F8F8',
        '#D33D3D',
        '#FDB913',
        '#42C184',
        '#004CE0',
        '#E82B71',
      ],
    },
    colorsHistoryKey: {
      type: String,
      default: 'vue-colorpicker-history',
    },
  },
  emits: ['cancel', 'changeColor', 'apply'],
  data() {
    return {
      hueWidth: 208,
      hueHeight: 8,
      previewHeight: 30,
      modelRgba: '',
      modelHex: '',
      r: 0,
      g: 0,
      b: 0,
      a: 1,
      h: 0,
      s: 0,
      v: 0,
      oldValue: '',
    };
  },
  computed: {
    isLightTheme() {
      return this.theme === 'light';
    },
    totalWidth() {
      return this.hueHeight + (this.hueWidth + 8) * 2;
    },
    previewWidth() {
      return this.totalWidth - (this.suckerHide ? 0 : this.previewHeight);
    },
    rgba() {
      return {
        r: this.r,
        g: this.g,
        b: this.b,
        a: this.a,
      };
    },
    hsv() {
      return {
        h: this.h,
        s: this.s,
        v: this.v,
      };
    },
    rgbString() {
      return `rgb(${this.r}, ${this.g}, ${this.b})`;
    },
    rgbaStringShort() {
      return `${this.r}, ${this.g}, ${this.b}, ${this.a}`;
    },
    rgbaString() {
      return `rgba(${this.rgbaStringShort})`;
    },
    hexString() {
      return this.rgb2hex(this.rgba, true);
    },
  },
  // watch: {
  //   color(newVal, oldVal) {
  //     if (newVal !== oldVal) {
  //       this.oldValue = newVal;
  //       Object.assign(this, this.setColorValue(this.color));
  //     }
  //   },
  // },
  created() {
    this.oldValue = this.color;
    Object.assign(this, this.setColorValue(this.color));
    this.setText();

    this.$watch('rgba', () => {
      this.$emit('changeColor', {
        rgba: this.rgba,
        hsv: this.hsv,
        hex: this.modelHex,
      });
    });
  },
  methods: {
    colorOutSize() {
      this.$emit('cancel');
    },
    cancel() {
      this.$emit('cancel');
    },
    apply() {
      this.$emit('apply', {
        rgba: this.rgba,
        hsv: this.hsv,
        hex: this.modelHex,
      });
    },
    selectSaturation(color) {
      const { r, g, b, h, s, v } = this.setColorValue(color);
      Object.assign(this, { r, g, b, h, s, v });
      this.setText();
    },
    selectHue(color) {
      const { r, g, b, h, s, v } = this.setColorValue(color);
      Object.assign(this, { r, g, b, h, s, v });
      this.setText();
      this.$nextTick(() => {
        this.$refs.saturation.renderColor();
      });
    },
    selectAlpha(a) {
      this.a = a;
      this.setText();
    },
    inputHex(color) {
      const { r, g, b, a, h, s, v } = this.setColorValue(color);
      Object.assign(this, { r, g, b, a, h, s, v });
      // this.modelHex = color;
      this.modelRgba = this.rgbaStringShort;
      this.$nextTick(() => {
        this.$refs.saturation.renderColor();
        this.$refs.saturation.renderSlide();
        this.$refs.hue.renderSlide();
      });
      if (!this.oldValue) {
        this.apply();
      }
    },
    inputRgba(color) {
      const { r, g, b, a, h, s, v } = this.setColorValue(color);
      Object.assign(this, { r, g, b, a, h, s, v });
      this.modelHex = this.hexString;
      this.modelRgba = color;
      this.$nextTick(() => {
        this.$refs.saturation.renderColor();
        this.$refs.saturation.renderSlide();
        this.$refs.hue.renderSlide();
      });
    },
    setText() {
      this.modelHex = this.hexString;
      this.modelRgba = this.rgbaStringShort;
    },
    openSucker(isOpen) {
      this.$emit('openSucker', isOpen);
    },
    selectSucker(color) {
      const { r, g, b, a, h, s, v } = this.setColorValue(color);
      Object.assign(this, { r, g, b, a, h, s, v });
      this.setText();
      this.$nextTick(() => {
        this.$refs.saturation.renderColor();
        this.$refs.saturation.renderSlide();
        this.$refs.hue.renderSlide();
      });
    },
    selectColor(color) {
      const { r, g, b, a, h, s, v } = this.setColorValue(color);
      Object.assign(this, { r, g, b, a, h, s, v });
      this.setText();
      this.$nextTick(() => {
        this.$refs.saturation.renderColor();
        this.$refs.saturation.renderSlide();
        this.$refs.hue.renderSlide();
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
.hu-color-picker {
  width: 232px;
  background: $black3;
  border-radius: 4px;
  box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.16);
  z-index: $z-index50;
  padding: 12px;

  &.light {
    background: #f7f8f9;

    .color-show {
      .sucker {
        background: #eceef0;
      }
    }

    .color-type {
      .name {
        background: #e7e8e9;
      }

      .value {
        color: #666;
        background: #eceef0;
      }
    }

    .colors.history {
      border-top: 1px solid #eee;
    }
  }

  canvas {
    vertical-align: top;
    border-radius: 5px;
    display: inline;
    vertical-align: unset;
  }

  .color-show {
    margin-top: 8px;
    display: flex;
  }
}

.gf_color-picker {
  background: map-get($hybrid-shade-dark, '200');
  box-shadow: 0 2px 32px rgba(0, 0, 0, 0.16), 0 6px 6px rgba(0, 0, 0, 0.06);
  border-radius: $radius;
  padding: 8px;
  width: 224px;

  &--header {
    display: flex;
    justify-content: space-between;
    height: 32px;

    &-title {
      color: map-get($hybrid-shade-light, '500');
    }

    &-close {
      cursor: pointer;
    }
  }
}
</style>
